/**
 * SPEC.md §3, §7, §9 — the topbar's Share menu, disabled since M0. M6.2
 * (issue #29) turns it into a real dropdown, mirroring `NewMenu.tsx`'s
 * shape and built on the same `useMenu` hook (M5.1, issue #21).
 *
 * `encodeShareState` only runs while the menu is actually open — App.tsx's
 * own debounced hash sync already recomputes it on every source change for
 * the address bar; doing the same unconditionally here too would mean
 * compressing the full diagram on every keystroke just to keep a closed
 * dropdown's hidden state current.
 */
import { ChevronDown, Share2 } from 'lucide-react';
import { encodeShareState } from '../lib/share';
import { useMenu } from './useMenu';

interface ShareMenuProps {
  source: string;
  diagramTheme: string;
  look: string;
  /** Called with a §9-style confirmation string ("Copied link.") after a successful copy. */
  onCopy: (message: string) => void;
}

export default function ShareMenu({ source, diagramTheme, look, onCopy }: ShareMenuProps) {
  const { isOpen, containerRef, toggle, close } = useMenu<HTMLDivElement>();
  const encoded = isOpen ? encodeShareState({ code: source, diagramTheme, look, version: 1 }) : null;

  function handleCopyLink() {
    if (encoded === null) return;
    const url = `${window.location.origin}${window.location.pathname}#${encoded}`;
    navigator.clipboard
      .writeText(url)
      .then(() => onCopy('Copied link.'))
      .catch((err: unknown) => console.error('Copy link failed:', err));
    close();
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggle}
        disabled={source.trim() === ''}
        className="flex items-center gap-1.5 rounded-sm px-2 py-1 text-xs text-text-ink hover:bg-ink-3 disabled:cursor-not-allowed disabled:text-ink-4 disabled:hover:bg-transparent"
      >
        <Share2 size={13} aria-hidden />
        Share
        <ChevronDown size={12} aria-hidden />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="Share"
          className="absolute right-0 top-full z-10 mt-1 w-56 rounded-sm border border-ink-3 bg-ink-2 py-1 shadow-lg"
        >
          {encoded === null ? (
            <p className="px-3 py-1.5 text-xs text-mute">Too large to link — export instead.</p>
          ) : (
            <button
              type="button"
              role="menuitem"
              onClick={handleCopyLink}
              className="block w-full px-3 py-1.5 text-left text-xs text-text-ink hover:bg-ink-3"
            >
              Copy link
            </button>
          )}
        </div>
      )}
    </div>
  );
}
